import React from "react";
import { motion } from "framer-motion";
import { COMPANIES, COMPANY_INFO } from "@/data/site";

// Hero network diagram: central hex logo + 6 orbiting company nodes with animated glow lines.
export default function HeroNetwork() {
  // Positions for 6 nodes around center on a 500x500 SVG
  const cx = 250, cy = 250, r = 175;
  const nodes = COMPANIES.map((c, i) => {
    const angle = (Math.PI * 2 * i) / COMPANIES.length - Math.PI / 2;
    return {
      ...c,
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  });

  return (
    <div className="relative w-full max-w-[540px] aspect-square" data-testid="hero-network">
      {/* Faint grid backdrop */}
      <div className="absolute inset-0 hero-grid opacity-30 rounded-full" />
      {/* Concentric glow rings */}
      <div className="absolute inset-6 rounded-full border border-blue-400/10" />
      <div className="absolute inset-16 rounded-full border border-blue-400/10" />
      <div className="absolute inset-28 rounded-full border border-blue-400/20" />

      <svg viewBox="0 0 500 500" className="relative w-full h-full">
        <defs>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.4" />
            <stop offset="70%" stopColor="#2563EB" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#0A2540" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        {/* Center glow */}
        <circle cx={cx} cy={cy} r={90} fill="url(#centerGlow)" />

        {/* Lines from center to each node */}
        {nodes.map((n, i) => (
          <g key={`line-${i}`}>
            <line x1={cx} y1={cy} x2={n.x} y2={n.y} stroke="url(#lineGrad)" strokeWidth="1.2" strokeDasharray="4 4">
              <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.6s" repeatCount="indefinite" />
            </line>
          </g>
        ))}

        {/* Small orbital dots */}
        {Array.from({ length: 30 }).map((_, i) => {
          const a = (Math.PI * 2 * i) / 30;
          const rr = 210 + Math.sin(i) * 8;
          return <circle key={i} cx={cx + rr * Math.cos(a)} cy={cy + rr * Math.sin(a)} r="1.2" fill="#60A5FA" opacity="0.35" />;
        })}
      </svg>

      {/* Center hex logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="w-[140px] h-[140px] hex-clip bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] flex items-center justify-center shadow-2xl shadow-blue-500/40">
            <div className="w-[130px] h-[130px] hex-clip bg-[#0A2540] flex flex-col items-center justify-center">
              <div className="w-9 h-9 hex-clip bg-[#2563EB] flex items-center justify-center mb-1">
                <span className="text-white font-extrabold text-base" style={{ fontFamily: "Outfit" }}>E</span>
              </div>
              <div className="text-white text-[11px] font-extrabold tracking-wide" style={{ fontFamily: "Outfit" }}>EASYWORKS</div>
              <div className="text-slate-400 text-[7px] tracking-[0.2em] font-semibold mt-0.5">INDUSTRIES PVT. LTD.</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Company nodes */}
      {nodes.map((n, i) => {
        const Icon = n.icon;
        // convert svg coords (500x500) to percent
        const leftPct = (n.x / 500) * 100;
        const topPct = (n.y / 500) * 100;
        const isLeft = n.x < 250;
        return (
          <motion.div
            key={n.key}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
            className="absolute -translate-x-1/2 -translate-y-1/2 pulse-node"
            style={{ left: `${leftPct}%`, top: `${topPct}%` }}
            data-testid={`hero-node-${n.key}`}
          >
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-11 h-11 rounded-full bg-[#0A2540] border-2 border-[#2563EB] flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Icon className="w-5 h-5 text-blue-300" />
              </div>
              <div className={`text-center ${isLeft ? "text-right" : "text-left"} whitespace-nowrap`}>
                <div className="text-white text-[11px] font-semibold">{n.name}</div>
                <div className="text-blue-300/70 text-[9px]">{n.tag}</div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
